import axios from "axios";

// Base URL for API calls
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:4001/api/v1";

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Add response interceptor to handle common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

const sourcingService = {
  // INTEGRATION MANAGEMENT

  // Get all integrations
  getAllIntegrations: async (params = {}) => {
    try {
      const response = await api.get("/sourcing/integrations", { params });
      return response.data.data;
    } catch (error) {
      console.error("Error fetching integrations:", error);
      throw error;
    }
  },

  // Get single integration by ID
  getIntegrationById: async (id) => {
    try {
      const response = await api.get(`/sourcing/integrations/${id}`);
      return response.data.data;
    } catch (error) {
      console.error("Error fetching integration:", error);
      throw error;
    }
  },

  // Create new integration
  createIntegration: async (integrationData) => {
    try {
      const response = await api.post(
        "/sourcing/integrations",
        integrationData,
      );
      return response.data.data;
    } catch (error) {
      console.error("Error creating integration:", error);
      throw error;
    }
  },

  // Update integration
  updateIntegration: async (id, integrationData) => {
    try {
      const response = await api.put(
        `/sourcing/integrations/${id}`,
        integrationData,
      );
      return response.data.data;
    } catch (error) {
      console.error("Error updating integration:", error);
      throw error;
    }
  },

  // Delete integration
  deleteIntegration: async (id) => {
    try {
      const response = await api.delete(`/sourcing/integrations/${id}`);
      return response.data.data;
    } catch (error) {
      console.error("Error deleting integration:", error);
      throw error;
    }
  },

  // Test integration connection
  testIntegration: async (id) => {
    try {
      const response = await api.post(`/sourcing/integrations/${id}/test`);
      return response.data.data;
    } catch (error) {
      console.error("Error testing integration:", error);
      throw error;
    }
  },

  // JOB POSTING MANAGEMENT

  // Post job to integration
  postJobToIntegration: async (jobData) => {
    try {
      const response = await api.post("/sourcing/jobs/post", jobData);
      return response.data.data;
    } catch (error) {
      console.error("Error posting job:", error);
      throw error;
    }
  },

  // Get job postings
  getJobPostings: async (params = {}) => {
    try {
      const response = await api.get("/sourcing/jobs/postings", { params });
      return response.data.data;
    } catch (error) {
      console.error("Error fetching job postings:", error);
      throw error;
    }
  },

  // Update job posting status
  updateJobPostingStatus: async (id, statusData) => {
    try {
      const response = await api.put(
        `/sourcing/jobs/postings/${id}/status`,
        statusData,
      );
      return response.data.data;
    } catch (error) {
      console.error("Error updating job posting status:", error);
      throw error;
    }
  },

  // Sync job posting metrics
  syncJobPostingMetrics: async (id) => {
    try {
      const response = await api.post(`/sourcing/jobs/postings/${id}/sync`);
      return response.data.data;
    } catch (error) {
      console.error("Error syncing job posting metrics:", error);
      throw error;
    }
  },

  // CANDIDATE MANAGEMENT

  // Search candidates from integration
  searchCandidates: async (searchData) => {
    try {
      const response = await api.post(
        "/sourcing/candidates/search",
        searchData,
      );
      return response.data.data;
    } catch (error) {
      console.error("Error searching candidates:", error);
      throw error;
    }
  },

  // Get candidate profiles
  getCandidateProfiles: async (params = {}) => {
    try {
      const response = await api.get("/sourcing/candidates", { params });
      return response.data.data;
    } catch (error) {
      console.error("Error fetching candidate profiles:", error);
      throw error;
    }
  },

  // Get candidates for specific job
  getCandidatesForJob: async (requisitionId, params = {}) => {
    try {
      const response = await api.get(
        `/sourcing/candidates/job/${requisitionId}`,
        { params },
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching candidates for job:", error);
      throw error;
    }
  },

  // Update candidate status
  updateCandidateStatus: async (id, statusData) => {
    try {
      const response = await api.put(
        `/sourcing/candidates/${id}/status`,
        statusData,
      );
      return response.data.data;
    } catch (error) {
      console.error("Error updating candidate status:", error);
      throw error;
    }
  },

  // Import candidate profile
  importCandidateProfile: async (candidateData) => {
    try {
      const response = await api.post(
        "/sourcing/candidates/import",
        candidateData,
      );
      return response.data.data;
    } catch (error) {
      console.error("Error importing candidate profile:", error);
      throw error;
    }
  },
};

export default sourcingService;
